import { useState, useCallback } from "react";
import {
  CustomerEntity,
  CreateCustomerUseCase,
  UpdateCustomerUseCase,
} from "../../domain";
import { CustomerRepositoryImpl } from "../../data/repositories/CustomerRepositoryImpl";

// Create repository instance (singleton)
const customerRepository = new CustomerRepositoryImpl();

// Create use cases
const createCustomerUseCase = new CreateCustomerUseCase(customerRepository);
const updateCustomerUseCase = new UpdateCustomerUseCase(customerRepository);

export interface UseCustomerOperationsReturn {
  loading: boolean;
  error: string | null;

  createCustomer: (
    customerData: Partial<CustomerEntity>
  ) => Promise<CustomerEntity | null>;
  updateCustomer: (
    customerId: string,
    customerData: Partial<CustomerEntity>
  ) => Promise<CustomerEntity | null>;

  clearError: () => void;
}

export const useCustomerOperations = (): UseCustomerOperationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new customer
   */
  const createCustomer = useCallback(
    async (
      customerData: Partial<CustomerEntity>
    ): Promise<CustomerEntity | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await createCustomerUseCase.execute(customerData);

        if (!result.success || !result.data) {
          throw new Error(result.error || "Failed to create customer");
        }

        return result.data;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to create customer";
        setError(errorMessage);
        console.error("Failed to create customer:", error);
        // Re-throw the error so caller can handle it
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Update an existing customer
   */
  const updateCustomer = useCallback(
    async (
      customerId: string,
      customerData: Partial<CustomerEntity>
    ): Promise<CustomerEntity | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateCustomerUseCase.execute(
          customerId,
          customerData
        );

        if (!result.success || !result.data) {
          throw new Error(result.error || "Failed to update customer");
        }

        return result.data;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to update customer";
        setError(errorMessage);
        console.error("Failed to update customer:", error);
        // Re-throw the error so caller can handle it
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    createCustomer,
    updateCustomer,
    clearError,
  };
};
